'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { isInternalLink, extractInternalPath } from '../utils/linkUtils';

/**
 * Global hook that intercepts clicks on links in content areas
 * and handles internal links with React Router navigation.
 * 
 * This ensures all internal links open in the same tab using
 * React Router, even when rendered via dangerouslySetInnerHTML.
 */
export const useSmartLinks = (contentRef: React.RefObject<HTMLElement>) => {
  const router = useRouter();

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      
      if (!link) return;
      
      const href = link.getAttribute('href');
      if (!href) return;

      if (link.getAttribute('target') === '_blank') {
        return;
      }
      
      // Skip if it's a button-style link (handled by AffiliateButton)
      if (link.classList.contains('btn-primary') || 
          link.classList.contains('affiliate-button')) {
        return;
      }
      
      // Check if it's an internal link
      const isInternal = isInternalLink(href);
      
      if (isInternal) {
        // Prevent default navigation
        e.preventDefault();
        
        // Extract path if it's a full URL
        const path = extractInternalPath(href);
        
        // Navigate using Next.js router
        router.push(path);
      }
      // External links will use default behavior (target="_blank" handled by processLinksInHtml)
    };

    container.addEventListener('click', handleLinkClick);
    
    return () => {
      container.removeEventListener('click', handleLinkClick);
    };
  }, [contentRef, router]);
};

